var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function _objectWithoutProperties(obj, keys) {
  var target = {};
  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }
  return target;
}
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import cx from 'classnames';
import useSize from '@react-hook/size';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import IconButton from '@material-ui/core/IconButton';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import Link from '@material-ui/core/Link';
import Divider from '@material-ui/core/Divider';
import Tooltip from '../Tooltip/Tooltip';
import { withBlank } from '../utils';
import { isLast } from '../Chips/utils';
var useStyles = makeStyles(function (theme) {
  return {
    card: {
      borderBottom: '1px solid ' + theme.palette.grey[700],
      padding: theme.spacing(0, 0, 2, 0),
      '& mark': {
        color: 'inherit',
        borderBottom: '2px solid ' + (R.path(['palette', 'highlight', 'hl2'])(theme) || theme.palette.success.dark)
      }
    },
    cardHeader: {
      padding: theme.spacing(1, 0, 0, 0)
    },
    cardContent: {
      padding: theme.spacing(0.5, 0),
      '&:last-child': {
        padding: theme.spacing(0.5, 0)
      }
    },
    cardActions: {
      padding: theme.spacing(0.5, 0, 0, 0),
      justifyContent: 'space-between'
    },
    header: {
      display: 'flex',
      alignItems: 'center'
    },
    rightSide: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '100%',
      padding: theme.spacing(0.5, 0)
    },
    dataset: _extends({}, R.pathOr({}, ['mixins', 'dataflow', 'title'], theme), {
      '&:hover': {
        color: theme.palette.primary.light,
        textDecoration: 'none' // remove this line when blueprint css import will no longer be in the DE
      }
    }),
    noDisplay: {
      display: 'none'
    },
    hidden: {
      visibility: 'hidden'
    },
    descriptionHighlight: {
      padding: 0
    },
    footer: {
      padding: theme.spacing(0.5, 0, 0, 0),
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    buttonLabel: {
      flexDirection: 'column'
    },
    divider: {
      width: 100,
      margin: theme.spacing(0.5, 0.5)
    },
    fadeContent: {
      color: theme.palette.grey['A200'],
      position: 'relative',
      overflow: 'hidden'
    },
    fading: {
      position: 'absolute',
      left: 0,
      right: 0,
      height: 15,
      margin: 0,
      padding: 0,
      backgroundImage: 'linear-gradient(to bottom, transparent, white)'
    },
    wrapper: {
      height: '100%'
    },
    container: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    mainContent: {
      minWidth: '200px',
      margin: theme.spacing(0, 0.5)
    },
    mutedText: _extends({}, R.pathOr({}, ['mixins', 'dataflow', 'desc', 'mutedText'], theme)),
    unmutedText: _extends({}, R.pathOr({}, ['mixins', 'dataflow', 'desc', 'unmutedText'], theme)),
    categories: {
      maxWidth: '100%',
      whiteSpace: 'noWrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    tooltipIndicator: {
      borderBottom: '1px dotted ' + theme.palette.primary.main
    }
  };
});
var BasicHTMLRenderer = function BasicHTMLRenderer(_ref) {
  var html = _ref.html,
    rest = _objectWithoutProperties(_ref, ['html']);
  return React.createElement('span', _extends({
    dangerouslySetInnerHTML: {
      __html: html
    }
  }, rest));
};
BasicHTMLRenderer.propTypes = process.env.NODE_ENV !== "production" ? {
  html: PropTypes.string
} : {};
var Dataflow = function Dataflow(_ref2) {
  var _cx, _cx2, _cx3;
  var testId = _ref2.testId,
    title = _ref2.title,
    body = _ref2.body,
    url = _ref2.url,
    handleUrl = _ref2.handleUrl,
    label = _ref2.label,
    labels = _ref2.labels,
    _ref2$dimensions = _ref2.dimensions,
    dimensions = _ref2$dimensions === undefined ? [] : _ref2$dimensions,
    _ref2$highlights = _ref2.highlights,
    highlights = _ref2$highlights === undefined ? [] : _ref2$highlights,
    _ref2$categories = _ref2.categories,
    categories = _ref2$categories === undefined ? [] : _ref2$categories,
    children = _ref2.children,
    _ref2$HTMLRenderer = _ref2.HTMLRenderer,
    HTMLRenderer = _ref2$HTMLRenderer === undefined ? BasicHTMLRenderer : _ref2$HTMLRenderer,
    id = _ref2.id;
  var classes = useStyles();
  var _useState = useState(false),
    isExpanded = _useState[0],
    setExpand = _useState[1];
  var description = R.propOr('', 'description', body);
  var target = React.useRef();
  var _useSize = useSize(target),
    height = _useSize[1];
  var handleExpand = function handleExpand() {
    setExpand(function (prev) {
      return !prev;
    });
  };
  var collapsedSize = R.min(height, 70);
  var needEllipse = collapsedSize >= 70;
  var mapIndexed = R.addIndex(R.map);
  return React.createElement(Card, {
    elevation: 0,
    square: true,
    className: classes.card,
    'data-testid': '' + testId
  }, React.createElement('div', {
    className: classes.container
  }, React.createElement('div', {
    className: classes.mainContent
  }, React.createElement(CardHeader, {
    className: classes.cardHeader,
    title: React.createElement('div', {
      className: classes.header,
      'data-testid': testId + '_title'
    }, React.createElement(Typography, {
      variant: 'h6',
      color: 'primary',
      display: 'inline'
    }, React.createElement(Link, {
      className: classes.dataset,
      href: url,
      onClick: handleUrl,
      color: 'primary',
      underline: 'none'
    }, React.createElement(HTMLRenderer, {
      html: title
    }))))
  }), R.not(R.isEmpty(description)) && React.createElement(Collapse, {
    'in': isExpanded,
    collapsedSize: collapsedSize,
    classes: isExpanded ? undefined : {
      wrapper: classes.wrapper
    }
  }, React.createElement(CardContent, {
    ref: target,
    className: cx(classes.cardContent, (_cx = {}, _cx[classes.fadeContent] = !isExpanded, _cx))
  }, React.createElement(Typography, {
    variant: 'body2',
    align: 'justify',
    className: cx(classes.container, classes.mutedText, (_cx2 = {}, _cx2[classes.unmutedText] = isExpanded, _cx2))
  }, React.createElement(HTMLRenderer, {
    html: description
  })), !isExpanded && needEllipse && React.createElement('div', {
    className: classes.fading,
    style: {
      top: collapsedSize - 15
    }
  }))), R.not(R.empty(R.propOr('', 'highlightDescription')(body))) && React.createElement(CardContent, {
    className: classes.descriptionHighlight
  }, React.createElement(Typography, {
    variant: 'body2',
    align: 'justify',
    className: classes.container
  }, React.createElement(HTMLRenderer, {
    html: R.propOr('', 'highlightDescription')(body),
    className: cx((_cx3 = {}, _cx3[classes.noDisplay] = isExpanded, _cx3))
  }))), React.createElement(CardContent, {
    className: classes.cardContent
  }, !R.isEmpty(dimensions) && React.createElement(Typography, {
    variant: 'body2',
    color: 'textSecondary',
    'data-testid': testId + '_dimensions'
  }, React.createElement('strong', null, R.prop('dimensions')(labels), ':\xA0'), React.createElement(HTMLRenderer, {
    html: R.join(', ', dimensions)
  })), R.map(function (_ref3) {
    var field = _ref3[0],
      fieldHighlights = _ref3[1];
    return React.createElement(Typography, {
      variant: 'body2',
      key: field,
      color: 'textSecondary',
      'data-testid': testId + '_highlight_' + field
    }, React.createElement('strong', null, field, ':\xA0'), R.map(function (fieldHighlight) {
      return React.createElement(HTMLRenderer, {
        key: id + '-' + field,
        html: fieldHighlight
      });
    }, fieldHighlights));
  })(highlights), React.createElement(Collapse, {
    'in': isExpanded
  }, React.createElement(Divider, {
    className: classes.divider
  }), R.map(function (_ref4) {
    var field = _ref4[0],
      values = _ref4[1];
    return React.createElement(Typography, {
      variant: 'body2',
      key: field,
      color: 'textSecondary',
      className: classes.categories
    }, React.createElement('strong', null, field, ':\xA0'), React.createElement('span', null, mapIndexed(function (category, idx) {
      return R.is(Array)(category) ? React.createElement(Tooltip, {
        key: 'category-tooltip-value-' + idx,
        title: R.join(' > ', category)
      }, React.createElement('span', null, React.createElement('span', {
        className: classes.tooltipIndicator
      }, ' ', '... > ' + R.last(category)), isLast(idx, values) ? '' : ', ')) : '' + R.identity(category) + (isLast(idx, values) ? '' : ', ');
    }, values)));
  })(categories), label && React.createElement('div', null, React.createElement(Typography, {
    variant: 'body2',
    color: 'textSecondary'
  }, React.createElement('strong', null, R.prop('source')(labels), ':\xA0'), ' ', label), React.createElement(Divider, {
    className: classes.divider
  })), React.createElement(CardActions, {
    className: classes.footer,
    disableSpacing: true
  }, React.createElement(Typography, {
    variant: 'caption',
    color: 'textSecondary'
  }, React.createElement('strong', null, R.prop('lastUpdated')(labels), '\xA0'), ' ', React.createElement('span', null, R.prop('date')(labels))), React.createElement(Typography, {
    variant: 'caption',
    color: 'textSecondary'
  }, R.prop('note')(labels)))))), React.createElement('div', null, React.createElement(CardActions, {
    className: classes.rightSide,
    disableSpacing: true
  }, React.createElement(IconButton, {
    'data-testid': 'dataflow-expand-button-test-id',
    'aria-label': R.propOr('Show details above', 'expand')(labels),
    'aria-expanded': isExpanded,
    onClick: function onClick() {
      return handleExpand();
    },
    color: 'primary',
    size: 'small'
  }, isExpanded ? React.createElement(ExpandLess, {
    'data-testid': 'dataflow-not-expand-test-id'
  }) : React.createElement(ExpandMore, {
    'data-testid': 'dataflow-expanded-test-id'
  })), children && children))));
};
Dataflow.propTypes = process.env.NODE_ENV !== "production" ? {
  testId: PropTypes.string,
  id: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.shape({
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    highlightDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
  }),
  url: PropTypes.string,
  handleUrl: PropTypes.func,
  label: PropTypes.string,
  labels: PropTypes.shape({
    source: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    lastUpdated: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    note: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
  }),
  highlights: PropTypes.array,
  dimensions: PropTypes.array,
  categories: PropTypes.array,
  children: PropTypes.node,
  HTMLRenderer: PropTypes.func
} : {};
export default withBlank(function (_ref5) {
  var isBlank = _ref5.isBlank,
    _ref5$title = _ref5.title,
    title = _ref5$title === undefined ? '' : _ref5$title;
  return {
    isBlank: R.or(isBlank, R.isEmpty(title))
  };
})(Dataflow);